<template>
  <div class="filters-bar">
    <div class="filter-group filter-dates">
      <span class="filter-label">📅 Desde</span>
      <input
        v-model="localFilters.start_date"
        :min="dateRange.min"
        :max="dateRange.max"
        class="filter-input"
        type="date"
      >
      <span class="filter-label">Hasta</span>
      <input
        v-model="localFilters.end_date"
        :min="dateRange.min"
        :max="dateRange.max"
        class="filter-input"
        type="date"
      >
    </div>

    <div class="presets">
      <button
        v-for="preset in presets"
        :key="preset.key"
        class="preset-btn"
        :class="{ active: activePreset === preset.key }"
        type="button"
        @click="applyPreset(preset)"
      >
        {{ preset.label }}
      </button>
    </div>

    <div class="filter-group">
      <span class="filter-label">Asesor</span>
      <filters-multi-select
        v-model="localFilters.device_groups"
        :items="deviceGroupOptions"
        empty-label="Todos los asesores"
        singular-label="asesor"
        plural-label="asesores"
        @input="handleDeviceGroupChange"
      />
    </div>

    <div class="filter-group">
      <span class="filter-label">Tienda</span>
      <filters-multi-select
        v-model="localFilters.devices"
        :items="availableDevices"
        empty-label="Todas las tiendas"
        singular-label="tienda"
        plural-label="tiendas"
      />
    </div>

    <div class="filter-group filter-calification">
      <span class="filter-label">Calificación de NPS</span>
      <input
        v-model="localNpsCalification"
        class="filter-input filter-input-short"
        type="text"
        placeholder="50,0"
        :disabled="!canEditNpsCalification || savingNpsCalification"
      >
      <button
        class="btn-filter"
        type="button"
        :disabled="!canEditNpsCalification || loading || savingNpsCalification"
        @click="emitSaveNpsCalification"
      >
        {{ savingNpsCalification ? 'Guardando…' : 'Guardar' }}
      </button>
    </div>

    <button class="btn-filter" type="button" :disabled="loading" @click="emitApply">Aplicar</button>
    <button class="btn-reset" type="button" :disabled="loading" @click="emitReset">↺ Reset</button>
  </div>
</template>

<script>
import FiltersMultiSelect from '@/components/client/rounds/nps-dashboard/components/FiltersMultiSelect.vue';

export default {
  name: 'NpsReplicaFiltersBar',
  components: {
    FiltersMultiSelect
  },
  props: {
    filtersMeta: {
      type: Object,
      default: function () {
        return {
          presets: [],
          device_groups: [],
          devices: [],
          device_group_device_map: {},
          date_range: {}
        };
      }
    },
    filters: {
      type: Object,
      default: function () {
        return {
          start_date: null,
          end_date: null,
          device_groups: [],
          devices: []
        };
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    npsCalification: {
      type: String,
      default: ''
    },
    canEditNpsCalification: {
      type: Boolean,
      default: true
    },
    savingNpsCalification: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      localFilters: this.cloneFilters(this.filters),
      activePreset: 'today',
      localNpsCalification: this.npsCalification
    };
  },
  computed: {
    presets () {
      return this.filtersMeta && this.filtersMeta.presets ? this.filtersMeta.presets : [];
    },
    deviceGroupOptions () {
      return this.filtersMeta && this.filtersMeta.device_groups ? this.filtersMeta.device_groups : [];
    },
    deviceOptions () {
      return this.filtersMeta && this.filtersMeta.devices ? this.filtersMeta.devices : [];
    },
    deviceGroupDeviceMap () {
      return this.filtersMeta && this.filtersMeta.device_group_device_map ? this.filtersMeta.device_group_device_map : {};
    },
    dateRange () {
      return this.filtersMeta && this.filtersMeta.date_range
        ? this.filtersMeta.date_range
        : { min: null, max: null, default_from: null, default_to: null };
    },
    availableDevices () {
      if (!this.localFilters.device_groups.length) {
        return this.deviceOptions;
      }

      const allowed = {};
      this.localFilters.device_groups.forEach((deviceGroup) => {
        const devices = this.deviceGroupDeviceMap[deviceGroup] || [];
        devices.forEach((device) => {
          allowed[device] = device;
        });
      });

      return this.deviceOptions.filter((deviceOption) => !!allowed[deviceOption.value]);
    }
  },
  watch: {
    filters: {
      deep: true,
      handler (value) {
        this.localFilters = this.cloneFilters(value);
        this.syncActivePreset();
      }
    },
    npsCalification (value) {
      this.localNpsCalification = value;
    }
  },
  methods: {
    cloneFilters (filters) {
      return {
        start_date: filters.start_date,
        end_date: filters.end_date,
        device_groups: (filters.device_groups || []).slice(),
        devices: (filters.devices || []).slice()
      };
    },
    emitApply () {
      this.enforceDateWindow();
      this.syncActivePreset();
      this.$emit('apply', this.cloneFilters(this.localFilters));
    },
    emitReset () {
      this.localFilters = {
        start_date: this.dateRange.default_from,
        end_date: this.dateRange.default_to,
        device_groups: [],
        devices: []
      };
      this.activePreset = 'today';
      this.$emit('reset');
    },
    emitSaveNpsCalification () {
      this.$emit('save-nps-calification', this.localNpsCalification);
    },
    applyPreset (preset) {
      this.localFilters.start_date = preset.from;
      this.localFilters.end_date = preset.to;
      this.activePreset = preset.key;
      this.$emit('preset', this.cloneFilters(this.localFilters));
    },
    handleDeviceGroupChange (values) {
      if (Array.isArray(values)) {
        this.localFilters.device_groups = values;
      }

      const allowedValues = this.availableDevices.map((device) => device.value);
      this.localFilters.devices = this.localFilters.devices.filter((device) => allowedValues.indexOf(device) !== -1);
    },
    enforceDateWindow () {
      if (!this.localFilters.start_date) {
        this.localFilters.start_date = this.dateRange.default_from;
      }

      if (!this.localFilters.end_date) {
        this.localFilters.end_date = this.dateRange.default_to;
      }

      if (this.dateRange.min && this.localFilters.start_date < this.dateRange.min) {
        this.localFilters.start_date = this.dateRange.min;
      }

      if (this.dateRange.max && this.localFilters.end_date > this.dateRange.max) {
        this.localFilters.end_date = this.dateRange.max;
      }

      if (this.localFilters.start_date > this.localFilters.end_date) {
        this.localFilters.start_date = this.localFilters.end_date;
      }
    },
    syncActivePreset () {
      const preset = this.presets.find((item) => item.from === this.localFilters.start_date && item.to === this.localFilters.end_date);
      this.activePreset = preset ? preset.key : null;
    }
  }
};
</script>

<style scoped>
.filters-bar {
  align-items: center;
  background: var(--bg-dark, #1A1A2E);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 28px;
  position: sticky;
  top: 0;
  z-index: 99;
}

.filter-group {
  align-items: center;
  display: flex;
  gap: 8px;
}

.filter-dates {
  flex-wrap: wrap;
}

.filter-calification {
  flex-wrap: wrap;
}

.filter-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
}

.filter-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  outline: none;
  padding: 5px 10px;
}

.filter-input-short {
  min-width: 90px;
  width: 90px;
}

.presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.preset-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 11px;
  padding: 4px 10px;
}

.preset-btn.active,
.preset-btn:hover {
  background: var(--accent, #E94560);
  border-color: var(--accent, #E94560);
  color: #fff;
}

.btn-filter,
.btn-reset {
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  padding: 6px 16px;
}

.btn-filter {
  background: var(--accent, #E94560);
  border: none;
  color: #fff;
  font-weight: 600;
}

.btn-reset {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.6);
  padding: 6px 12px;
}

.btn-filter:disabled,
.btn-reset:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 900px) {
  .filters-bar {
    padding: 12px 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/client/rounds/nps-dashboard/components/FiltersBar.vue